import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const ToastContext = createContext({
  showToast: () => {}
});

const DEFAULT_DURATION = 4200;

const uid = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((config) => {
    const entry = {
      id: uid(),
      type: config?.type || "info",
      message: config?.message || "Done",
      actionLabel: config?.actionLabel || null,
      onAction: typeof config?.onAction === "function" ? config.onAction : null,
      duration: Number(config?.duration) > 0 ? Number(config.duration) : DEFAULT_DURATION
    };

    setToasts((prev) => [...prev, entry]);

    window.setTimeout(() => {
      dismissToast(entry.id);
    }, entry.duration);

    return entry.id;
  }, [dismissToast]);

  const value = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            <span>{toast.message}</span>
            <div className="toast-actions">
              {toast.actionLabel && toast.onAction && (
                <button
                  className="btn-nav"
                  type="button"
                  onClick={() => {
                    toast.onAction();
                    dismissToast(toast.id);
                  }}
                >
                  {toast.actionLabel}
                </button>
              )}
              <button
                className="btn-nav"
                type="button"
                aria-label="Dismiss notification"
                onClick={() => dismissToast(toast.id)}
              >
                Dismiss
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
